(function () {
  const ROOT_SELECTOR = '[data-q-scroller-card]';

  class QScrollerCard {
    constructor(root) {
      this.root = root;
      this.bg = root.querySelector('.q-scroller-card__bg');
      this.track = root.querySelector('.q-scroller-card__track');
      this.title = root.querySelector('.q-scroller-card__title');
      this.cardsWrap = root.querySelector('.q-scroller-card__cards');

      if (!this.bg || !this.track || !this.cardsWrap) return;

      this.onScroll = this.onScroll.bind(this);
      this.onResize = this.onResize.bind(this);

      this.root.classList.add('is-ready');
      this.cardsRevealed = false;
      this.bgVisible = false;

      if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
        this.bg.classList.add('is-visible');
        this.root.classList.add('is-design-preview');
        this.showFinalState();
        return;
      }

      if (window.Shopify?.designMode) {
        this.bg.classList.add('is-visible');
        this.root.classList.add('is-animated', 'is-design-preview');
        this.showFinalState();
        return;
      }

      this.root.classList.add('is-animated');
      window.addEventListener('scroll', this.onScroll, { passive: true });
      window.addEventListener('resize', this.onResize, { passive: true });
      this.onResize();
    }

    destroy() {
      window.removeEventListener('scroll', this.onScroll);
      window.removeEventListener('resize', this.onResize);
      if (this.raf) cancelAnimationFrame(this.raf);
      this.root.classList.remove('is-animated', 'is-design-preview', 'is-ready');
      this.bg.classList.remove('is-visible');
      this.bgVisible = false;
      this.resetMotion();
    }

    getStickyTop() {
      const raw = getComputedStyle(document.documentElement)
        .getPropertyValue('--sticky-area-height')
        .trim();
      const parsed = parseFloat(raw);
      return Number.isFinite(parsed) ? parsed : 0;
    }

    readLengthPx(name, fallbackPx) {
      const raw = getComputedStyle(this.root).getPropertyValue(name).trim();
      const parsed = parseFloat(raw);
      return Number.isFinite(parsed) ? parsed : fallbackPx;
    }

    getTitleCardGap() {
      return this.readLengthPx('--q-scroller-title-card-gap', 32);
    }

    getBounceAmplitude() {
      return this.readLengthPx('--q-scroller-bounce-amplitude', 24);
    }

    getContentBlockHeight() {
      const titleH = this.title ? this.title.offsetHeight || 0 : 0;
      const cardsH = this.cardsWrap ? this.cardsWrap.offsetHeight || 0 : 0;
      return titleH + this.getTitleCardGap() + cardsH;
    }

    /** title + cards 整体在 stage 内垂直居中 */
    computeCenteredLayout() {
      const blockH = this.getContentBlockHeight();
      this.titleTopMax = Math.max(0, (this.stageHeight - blockH) / 2);
    }

    measure() {
      this.vh = window.innerHeight || document.documentElement.clientHeight || 800;
      this.stickyTop = this.getStickyTop();
      this.stageHeight = this.vh - this.stickyTop;

      this.titlePhase = this.readLengthPx('--q-scroller-title-phase', 180);
      this.bouncePhase = this.readLengthPx('--q-scroller-bounce', 48);
      this.cardsDelay = this.readLengthPx('--q-scroller-cards-delay', 0);
      this.cardsPhase = this.readLengthPx('--q-scroller-cards-slide', 180);
      this.cardsFadePhase = this.readLengthPx('--q-scroller-cards-fade', 72);
      this.exitPhase = this.readLengthPx('--q-scroller-exit', 180);

      this.computeCenteredLayout();

      this.titleSlideEnd = this.titlePhase;
      this.bounceEnd = this.titlePhase + this.bouncePhase;
      this.cardsStart = this.titleSlideEnd + this.cardsDelay;
      this.cardsTravel = this.bouncePhase + this.cardsPhase;
      this.meetEnd = this.cardsStart + this.cardsTravel;
      this.exitStart = this.meetEnd;
    }

    onResize() {
      this.measure();

      const trackHeight = this.stageHeight + this.exitStart + this.exitPhase;

      this.track.style.setProperty('--q-scroller-track-height', `${Math.round(trackHeight)}px`);
      this.root.style.setProperty('--q-scroller-stage-height', `${Math.round(this.stageHeight)}px`);

      this.onScroll();
    }

    onScroll() {
      if (this.raf) return;
      this.raf = requestAnimationFrame(() => {
        this.raf = null;
        this.update();
      });
    }

    getScrollProgress() {
      const rect = this.track.getBoundingClientRect();
      return Math.max(0, this.stickyTop - rect.top);
    }

    updateBgVisibility() {
      const rect = this.root.getBoundingClientRect();
      const intersects = rect.bottom > 0 && rect.top < this.vh;
      const fullyGone = rect.bottom <= 0 || rect.top >= this.vh;

      if (intersects) {
        this.bgVisible = true;
      } else if (this.bgVisible && fullyGone) {
        this.bgVisible = false;
      }

      return this.bgVisible;
    }

    getCardsFinalTop() {
      const titleH = this.title ? this.title.offsetHeight || 0 : 0;
      return this.titleTopMax + titleH + this.getTitleCardGap();
    }

    clamp(value, min, max) {
      return Math.min(max, Math.max(min, value));
    }

    easeOutCubic(t) {
      return 1 - Math.pow(1 - t, 3);
    }

    slideFromBottom(progress, duration, finalTop) {
      if (progress <= 0) {
        return { top: this.stageHeight, opacity: 0 };
      }

      const t = this.clamp(progress / duration, 0, 1);
      const top = this.stageHeight + (finalTop - this.stageHeight) * t;
      const opacity = this.clamp(t * 1.5, 0, 1);

      return { top, opacity };
    }

    titleBounceOffset(progress, duration) {
      const t = this.clamp(progress / (duration * 0.65), 0, 1);
      return Math.sin(t * Math.PI) * this.getBounceAmplitude();
    }

    applyTitle(top, opacity) {
      if (!this.title) return;
      this.title.style.transform = `translate3d(0, ${top}px, 0)`;
      this.title.style.opacity = String(opacity);
    }

    setCardsTop(top) {
      this.cardsWrap.style.top = `${top.toFixed(2)}px`;
    }

    holdCardsHidden() {
      this.setCardsTop(this.stageHeight);
      this.cardsWrap.style.opacity = '0';
      this.cardsWrap.classList.remove('is-revealed');
    }

    applyCardsMotion(cardsScrollY) {
      const finalTop = this.getCardsFinalTop();
      const travel = finalTop - this.stageHeight;
      const t = this.clamp(cardsScrollY / this.cardsTravel, 0, 1);
      const top = this.stageHeight + travel * t;

      const fadeT = this.clamp(cardsScrollY / this.cardsFadePhase, 0, 1);
      const opacity = this.easeOutCubic(fadeT);

      this.setCardsTop(top);
      this.cardsWrap.style.opacity = String(opacity);

      if (opacity > 0.05) {
        this.cardsWrap.classList.add('is-revealed');
        this.cardsRevealed = true;
      } else {
        this.cardsWrap.classList.remove('is-revealed');
      }
    }

    applyMeetState() {
      this.applyTitle(this.titleTopMax, 1);
      this.setCardsTop(this.getCardsFinalTop());
      this.cardsWrap.style.opacity = '1';
      this.cardsWrap.classList.add('is-revealed');
      this.cardsRevealed = true;
    }

    hideCards() {
      this.cardsRevealed = false;
      this.cardsWrap.classList.remove('is-revealed');
      this.cardsWrap.style.top = '';
      this.cardsWrap.style.opacity = '';
      this.cardsWrap.style.transform = '';
    }

    resetMotion() {
      this.hideCards();
      this.applyTitle(this.stageHeight, 0);
    }

    showFinalState() {
      this.computeCenteredLayout();
      this.applyMeetState();
    }

    updateTitle(scrollY) {
      const titleTop = this.titleTopMax;

      if (scrollY < this.titleSlideEnd) {
        const motion = this.slideFromBottom(scrollY, this.titlePhase, titleTop);
        this.applyTitle(motion.top, motion.opacity);
        return;
      }

      if (scrollY < this.bounceEnd) {
        const bounceY = scrollY - this.titleSlideEnd;
        this.applyTitle(titleTop - this.titleBounceOffset(bounceY, this.bouncePhase), 1);
        return;
      }

      this.applyTitle(titleTop, 1);
    }

    updateCards(scrollY) {
      if (scrollY < this.cardsStart) {
        this.holdCardsHidden();
        return;
      }

      if (scrollY >= this.meetEnd) {
        this.applyMeetState();
        return;
      }

      this.applyCardsMotion(scrollY - this.cardsStart);
    }

    update() {
      const bgActive = this.updateBgVisibility();
      this.bg.classList.toggle('is-visible', bgActive);

      if (!bgActive) {
        this.resetMotion();
        return;
      }

      this.computeCenteredLayout();

      const scrollY = this.getScrollProgress();

      if (scrollY <= 0) {
        this.resetMotion();
        return;
      }

      if (scrollY >= this.exitStart) {
        const exitY = scrollY - this.exitStart;
        const cardsFinalTop = this.getCardsFinalTop();
        this.applyTitle(this.titleTopMax - exitY, 1);
        this.setCardsTop(cardsFinalTop - exitY);
        this.cardsWrap.style.opacity = '1';
        this.cardsWrap.classList.add('is-revealed');
        this.cardsRevealed = true;
        return;
      }

      this.updateTitle(scrollY);
      this.updateCards(scrollY);
    }
  }

  const instances = new WeakMap();

  function initScrollerCards(scope = document) {
    scope.querySelectorAll(ROOT_SELECTOR).forEach((root) => {
      if (instances.has(root)) return;
      const instance = new QScrollerCard(root);
      if (instance.track) instances.set(root, instance);
    });
  }

  function destroyScrollerCards(scope) {
    scope.querySelectorAll(ROOT_SELECTOR).forEach((root) => {
      const instance = instances.get(root);
      if (instance) {
        instance.destroy();
        instances.delete(root);
      }
    });
  }

  function boot() {
    initScrollerCards(document);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  document.addEventListener('shopify:section:load', (event) => {
    initScrollerCards(event.target);
  });

  document.addEventListener('shopify:section:unload', (event) => {
    destroyScrollerCards(event.target);
  });
})();
